<template>
    <b-modal ref="workflowModal" :title="modalTitle" size="lg" class="workflow-modal" @hidden="handleHidden">
        <!-- 1단계: 기본 정보 입력 -->
        <div v-show="currentStep === 1" class="step1-form">
            <form @submit.prevent="handleStep1Next">
                <b-row>
                    <b-col cols="12" md="6">
                        <div class="form-group">
                            <label class="step1-form-label">조직 <span class="text-danger">*</span></label>
                            <b-form-select v-model="form.orgIdx" :options="orgOptions" class="form-control" required>
                                <template #first>
                                    <b-form-select-option :value="null">조직을 선택하세요</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>
                    </b-col>
                    <b-col cols="12" md="6">
                        <div class="form-group">
                            <label class="step1-form-label">워크플로우 이름 <span class="text-danger">*</span></label>
                            <input
                                v-model="form.workflowName"
                                type="text"
                                class="form-control"
                                placeholder="예: 2026년 탄소배출 산정"
                                required
                            />
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" md="6">
                        <div class="form-group">
                            <label class="step1-form-label">시작 월 <span class="text-danger">*</span></label>
                            <DatePicker
                                v-model="fromMonthFormatted"
                                type="month"
                                format="YYYY-MM"
                                value-type="format"
                                placeholder="----년 --월"
                                :clearable="false"
                                @change="handleFromMonthChange"
                            />
                        </div>
                    </b-col>
                    <b-col cols="12" md="6">
                        <div class="form-group">
                            <label class="step1-form-label">종료 월 <span class="text-danger">*</span></label>
                            <DatePicker
                                v-model="toMonthFormatted"
                                type="month"
                                format="YYYY-MM"
                                value-type="format"
                                placeholder="----년 --월"
                                :clearable="false"
                                @change="handleToMonthChange"
                            />
                        </div>
                    </b-col>
                </b-row>
                <div v-if="pathwayYear" class="form-group pathway-form-group">
                    <label class="step1-form-label">
                        연동 이행계획 (Pathway)
                        <span class="text-muted font-weight-normal">({{ pathwayYear }}년)</span>
                    </label>
                    <b-form-select
                        v-model="form.pathwayIdx"
                        :options="pathwaySelectOptions"
                        :disabled="pathwayFieldDisabled || pathwayLoading"
                        class="form-control"
                    >
                        <template #first>
                            <b-form-select-option :value="null">연동 안 함</b-form-select-option>
                        </template>
                    </b-form-select>
                    <div v-if="pathwayLoading" class="pathway-hint text-muted">
                        <b-spinner small class="mr-1" /> 이행계획 목록을 불러오는 중…
                    </div>
                    <div v-else-if="pathwayLoadError" class="pathway-hint text-danger">{{ pathwayLoadError }}</div>
                    <div v-else-if="pathwayFieldDisabled" class="pathway-hint text-muted">
                        확정된 워크플로우는 Pathway를 변경할 수 없습니다. 변경이 필요하면 재오픈 후 수정하세요.
                    </div>
                    <div v-else-if="pathwayOptions.length === 0" class="pathway-hint text-warning">
                        {{ pathwayYear }}년 이행계획이 없습니다. 배출 목표에서 연도별 이행계획을 먼저 등록하세요.
                    </div>
                    <div v-else-if="pathwayRecommendedIdx && form.pathwayIdx === pathwayRecommendedIdx" class="pathway-hint text-muted">
                        미사용 이행계획이 자동 추천되었습니다.
                    </div>
                </div>
                <div class="form-group">
                    <label class="step1-form-label">비고</label>
                    <textarea
                        v-model="form.remarks"
                        class="form-control"
                        rows="3"
                        placeholder="워크플로우에 대한 비고를 입력하세요"
                    ></textarea>
                </div>
            </form>
        </div>

        <!-- 2단계: 준비 상태 점검 -->
        <div v-show="currentStep === 2" class="pre-check-step">
            <p class="pre-check-desc">프로세스를 시작하기 전에 데이터의 정합성을 확인합니다.</p>
            <!-- 컨텍스트 헤더 -->
            <div class="pre-check-context">
                <div class="pre-check-context-left">
                    <div class="pre-check-context-icon">
                        <i class="fas fa-clipboard-list"></i>
                    </div>
                    <div class="pre-check-context-info">
                        <div class="pre-check-context-label">점검 대상 조직 및 기간</div>
                        <div class="pre-check-context-value">
                            <span class="context-org-name">{{ preCheckOrgName }}</span>
                            <span class="context-separator">|</span>
                            <span class="context-period">{{ preCheckPeriod }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 로딩 상태 -->
            <div v-if="preCheckLoading" class="pre-check-loading">
                <div class="loading-dots"><span></span><span></span><span></span></div>
                <span class="loading-text">준비 상태를 확인 중입니다...</span>
            </div>

            <!-- 에러 상태 -->
            <div v-else-if="preCheckError" class="pre-check-error">
                <div class="error-icon-wrap"><i class="fas fa-exclamation-circle"></i></div>
                <span>{{ preCheckError }}</span>
            </div>

            <template v-else>
                <!-- 점검 항목 목록 -->
                <div class="simple-cards-grid">
                    <div
                        v-for="(check, idx) in displayChecks"
                        :key="check.code || idx"
                        class="pre-check-card"
                        :class="{
                            'card-pass': check.status === 'PASS',
                            'card-warn': check.status === 'WARN',
                            'card-fail': check.status === 'FAIL',
                        }"
                    >
                        <div
                            class="card-status-badge"
                            :class="{
                                'badge-pass': check.status === 'PASS',
                                'badge-warn': check.status === 'WARN',
                                'badge-fail': check.status === 'FAIL',
                            }"
                        >
                            <i v-if="check.status === 'PASS'" class="fas fa-check-circle"></i>
                            <i v-else-if="check.status === 'WARN'" class="fas fa-exclamation-triangle"></i>
                            <i v-else class="fas fa-times-circle"></i>
                            <span>{{
                                check.status === "PASS" ? "통과" : check.status === "WARN" ? "경고" : "오류"
                            }}</span>
                        </div>
                        <div class="card-title">{{ check.title }}</div>
                        <div
                            class="card-message"
                            :class="{
                                'msg-pass': check.status === 'PASS',
                                'msg-warn': check.status === 'WARN',
                                'msg-fail': check.status === 'FAIL',
                            }"
                        >
                            {{ check.message }}
                        </div>
                        <div v-if="check.code === 'workflowPeriodConflict' && check.details" class="card-meta-pill">
                            <i class="fas fa-calendar-alt"></i>
                            {{ formatMonthRange(check.details.fromMonth, check.details.toMonth) }}
                        </div>
                        <div
                            v-else-if="check.code === 'emissionTarget' && check.details && check.details.yearRange"
                            class="card-meta-pill"
                        >
                            <i class="fas fa-bullseye"></i>
                            {{ check.details.yearRange }}
                        </div>
                        <div v-if="getSimpleCheckDetailText(check)" class="card-detail-text">
                            {{ getSimpleCheckDetailText(check) }}
                        </div>
                        <!-- factorMapping: 자동 매칭 버튼 -->
                        <div
                            v-if="check.code === 'factorMapping' && factorMappingUnmappedItems.length > 0"
                            class="card-auto-match"
                        >
                            <b-button
                                size="sm"
                                variant="primary"
                                class="card-auto-match-btn"
                                :disabled="autoMatchLoading"
                                @click="handleAutoMatch"
                            >
                                <b-spinner v-if="autoMatchLoading" small class="mr-1"></b-spinner>
                                <i v-else class="fas fa-magic mr-1"></i>
                                {{ autoMatchLoading ? "적용 중..." : "자동 매칭" }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <template #modal-footer>
            <template v-if="currentStep === 1">
                <b-button variant="secondary" @click="closeModal">취소</b-button>
                <b-button v-if="isEditMode" variant="primary" @click="handleSave" :disabled="saving"> 저장 </b-button>
                <b-button v-else variant="success" @click="handleStep1Next" :disabled="step1Loading"
                    >다음 단계</b-button
                >
            </template>
            <template v-else>
                <b-button variant="secondary" @click="goToStep1"> ← 이전 </b-button>
                <b-button v-if="hasUnmappedFactors" variant="outline-secondary" @click="handleCreateWorkflow">
                    미매핑 무시하고 생성
                </b-button>
                <b-button variant="success" @click="handleCreateWorkflow">
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    {{ saving ? "생성 중..." : "생성" }}
                </b-button>
            </template>
        </template>
    </b-modal>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonCodes from "../../../codes";
import carbonApi from "../../../api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";

export default {
    name: "WorkflowFormModal",
    components: {
        DatePicker,
    },
    props: {
        isEditMode: {
            type: Boolean,
            default: false,
        },
        initialData: {
            type: Object,
            default: () => ({}),
        },
        orgOptions: {
            type: Array,
            default: () => [],
        },
        saving: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: {
                orgIdx: null,
                workflowName: "",
                fromMonth: "",
                toMonth: "",
                remarks: "",
                pathwayIdx: null,
            },
            workflowIdx: null,
            periodStat: null,
            fromMonthFormatted: "",
            toMonthFormatted: "",
            pathwayOptions: [],
            pathwayRecommendedIdx: null,
            pathwayLoading: false,
            pathwayLoadError: null,
            currentStep: 1,
            step1Loading: false,
            preCheckLoading: false,
            preCheckError: null,
            preCheckData: null,
            expandedSections: {},
            autoMatchLoading: false,
        };
    },
    computed: {
        modalTitle() {
            if (this.currentStep === 2) return "워크플로우 생성 - 준비 상태 점검";
            return this.isEditMode ? "워크플로우 수정" : "워크플로우 생성";
        },
        preCheckOrgName() {
            const orgOpt = this.orgOptions.find((o) => o.value === this.form.orgIdx);
            return orgOpt?.text || "—";
        },
        preCheckPeriod() {
            const from = this.formatMonthForSubtitle(this.form.fromMonth);
            const to = this.formatMonthForSubtitle(this.form.toMonth);
            return `${from} ~ ${to}`;
        },
        displayChecks() {
            if (!this.preCheckData?.checks) return [];
            const order = [
                "workflowPeriodConflict",
                "orgBoundary",
                "operBoundary",
                "emissionSource",
                "factorMapping",
                "emissionTarget",
                "evidence",
            ];
            const checks = [...this.preCheckData.checks];
            checks.sort((a, b) => {
                const ia = order.indexOf(a.code);
                const ib = order.indexOf(b.code);
                return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
            });
            return checks;
        },
        factorMappingUnmappedItems() {
            const factorCheck = this.preCheckData?.checks?.find((c) => c.code === "factorMapping");
            return factorCheck?.details?.items || [];
        },
        hasUnmappedFactors() {
            return this.factorMappingUnmappedItems.length > 0;
        },
        pathwayYear() {
            const ym = this.form.fromMonth;
            if (!ym || ym.length < 4) return null;
            return ym.substring(0, 4);
        },
        pathwayFieldDisabled() {
            return (this.periodStat || "").toUpperCase() === "CONFIRMED";
        },
        pathwaySelectOptions() {
            return this.pathwayOptions.map((opt) => {
                const usedElsewhere = opt.isUsed && !this.isCurrentWorkflowPathway(opt.pathwayIdx);
                return {
                    value: opt.pathwayIdx,
                    text: this.formatPathwayOptionLabel(opt),
                    disabled: usedElsewhere,
                };
            });
        },
    },
    watch: {
        initialData: {
            immediate: true,
            handler(newVal) {
                if (newVal && Object.keys(newVal).length > 0) {
                    this.workflowIdx = newVal.workflowIdx != null ? Number(newVal.workflowIdx) : null;
                    this.periodStat = newVal.periodStat || null;
                    this.form = {
                        orgIdx: newVal.orgIdx || null,
                        workflowName: newVal.workflowName || "",
                        fromMonth: newVal.fromMonth || "",
                        toMonth: newVal.toMonth || "",
                        remarks: newVal.remarks || "",
                        pathwayIdx: newVal.pathwayIdx != null ? Number(newVal.pathwayIdx) : null,
                    };
                    if (this.form.fromMonth && this.form.fromMonth.length === 6) {
                        this.fromMonthFormatted = `${this.form.fromMonth.substring(0, 4)}-${this.form.fromMonth.substring(4, 6)}`;
                    }
                    if (this.form.toMonth && this.form.toMonth.length === 6) {
                        this.toMonthFormatted = `${this.form.toMonth.substring(0, 4)}-${this.form.toMonth.substring(4, 6)}`;
                    }
                    this.$nextTick(() => this.loadAvailablePathways());
                } else {
                    this.resetForm();
                }
            },
        },
        "form.orgIdx"() {
            this.loadAvailablePathways();
        },
        "form.fromMonth"() {
            this.loadAvailablePathways();
        },
    },
    methods: {
        show() {
            this.currentStep = 1;
            this.preCheckData = null;
            this.preCheckError = null;
            this.expandedSections = {};
            this.$refs.workflowModal.show();
            this.$nextTick(() => this.loadAvailablePathways());
        },
        hide() {
            this.$refs.workflowModal.hide();
        },
        closeModal() {
            this.hide();
        },
        handleHidden() {
            this.resetForm();
            this.currentStep = 1;
            this.preCheckData = null;
            this.preCheckError = null;
            this.$emit("hidden");
        },
        resetForm() {
            this.form = {
                orgIdx: null,
                workflowName: "",
                fromMonth: "",
                toMonth: "",
                remarks: "",
                pathwayIdx: null,
            };
            this.workflowIdx = null;
            this.periodStat = null;
            this.fromMonthFormatted = "";
            this.toMonthFormatted = "";
            this.pathwayOptions = [];
            this.pathwayRecommendedIdx = null;
            this.pathwayLoadError = null;
            this.pathwayLoading = false;
        },
        isCurrentWorkflowPathway(pathwayIdx) {
            return (
                this.isEditMode &&
                this.form.pathwayIdx != null &&
                Number(pathwayIdx) === Number(this.form.pathwayIdx)
            );
        },
        formatPathwayOptionLabel(opt) {
            const stat = CarbonCodes.goalStat.getLabel(opt.goalStat) || opt.goalStat || "-";
            const used =
                opt.isUsed && !this.isCurrentWorkflowPathway(opt.pathwayIdx)
                    ? " · 다른 워크플로 사용 중"
                    : "";
            return `${opt.pathwayYear}년 · ID ${opt.pathwayIdx} · ${stat}${used}`;
        },
        async loadAvailablePathways() {
            const orgIdx = this.form.orgIdx;
            const year = this.pathwayYear;
            if (!orgIdx || !year) {
                this.pathwayOptions = [];
                this.pathwayRecommendedIdx = null;
                this.pathwayLoadError = null;
                return;
            }
            this.pathwayLoading = true;
            this.pathwayLoadError = null;
            try {
                const res = await carbonApi.workflow.getAvailablePathways({ orgIdx, year });
                const data = unwrapResponse(res) || {};
                const options = Array.isArray(data.options) ? data.options : [];
                this.pathwayOptions = options.map((o) => ({
                    pathwayIdx: Number(o.pathwayIdx),
                    pathwayYear: o.pathwayYear,
                    goalStat: o.goalStat,
                    isUsed: !!o.isUsed,
                    usedByWorkflowIdx: o.usedByWorkflowIdx,
                }));
                const recommended = data.recommendedPathwayIdx != null ? Number(data.recommendedPathwayIdx) : null;
                this.pathwayRecommendedIdx = recommended;
                const currentValid = this.form.pathwayIdx != null &&
                    this.pathwayOptions.some((o) => o.pathwayIdx === Number(this.form.pathwayIdx));
                if (!currentValid && recommended != null && !this.pathwayFieldDisabled) {
                    this.form.pathwayIdx = recommended;
                }
            } catch (e) {
                this.pathwayOptions = [];
                this.pathwayRecommendedIdx = null;
                this.pathwayLoadError = extractErrorMessage(e, "이행계획 목록 조회에 실패했습니다.");
            } finally {
                this.pathwayLoading = false;
            }
        },
        formatMonthForSubtitle(ym) {
            if (!ym || ym.length !== 6) return "—";
            return `${ym.substring(0, 4)}.${ym.substring(4, 6)}`;
        },
        formatMonthRange(fromMonth, toMonth) {
            const from = this.formatMonthForSubtitle(fromMonth);
            const to = this.formatMonthForSubtitle(toMonth);
            return `${from} ~ ${to}`;
        },
        handleFromMonthChange(value) {
            this.form.fromMonth = value ? value.replace(/-/g, "") : "";
            if (!this.pathwayYear) {
                this.form.pathwayIdx = null;
            }
        },
        handleToMonthChange(value) {
            this.form.toMonth = value ? value.replace(/-/g, "") : "";
        },
        validateStep1() {
            if (!this.form.orgIdx || !this.form.workflowName || !this.form.fromMonth || !this.form.toMonth) {
                this.$bvToast.toast("필수 항목을 모두 입력해주세요.", {
                    title: "입력 오류",
                    variant: "warning",
                    autoHideDelay: 3000,
                });
                return false;
            }
            const monthPattern = /^[0-9]{6}$/;
            if (!monthPattern.test(this.form.fromMonth) || !monthPattern.test(this.form.toMonth)) {
                this.$bvToast.toast("시작 월과 종료 월을 올바르게 선택해주세요.", {
                    title: "입력 오류",
                    variant: "warning",
                    autoHideDelay: 3000,
                });
                return false;
            }
            if (parseInt(this.form.fromMonth, 10) > parseInt(this.form.toMonth, 10)) {
                this.$bvToast.toast("시작 월은 종료 월보다 작거나 같아야 합니다.", {
                    title: "입력 오류",
                    variant: "warning",
                    autoHideDelay: 3000,
                });
                return false;
            }
            return true;
        },
        async handleStep1Next() {
            if (!this.validateStep1()) return;
            if (this.isEditMode) {
                this.emitSave();
                return;
            }
            this.step1Loading = true;
            this.preCheckError = null;
            try {
                const res = await carbonApi.workflow.getPreCheck({
                    orgIdx: this.form.orgIdx,
                    fromMonth: this.form.fromMonth,
                    toMonth: this.form.toMonth,
                });
                const data = unwrapResponse(res);
                this.preCheckData = data;
                this.currentStep = 2;
                // 기본적으로 모든 섹션 닫힘
                this.expandedSections = {};
            } catch (e) {
                this.preCheckError = extractErrorMessage(e, "사전 점검 조회에 실패했습니다.");
                this.$bvToast.toast(this.preCheckError, {
                    title: "오류",
                    variant: "danger",
                    autoHideDelay: 4000,
                });
            } finally {
                this.step1Loading = false;
            }
        },
        goToStep1() {
            this.currentStep = 1;
        },
        getSimpleCheckDetailText(check) {
            if (!check.details) return "";
            if (check.code === "orgBoundary" && check.details.items?.length) {
                return this.formatOrgBoundaryDetails(check.details.items);
            }
            return "";
        },
        formatOrgBoundaryDetails(items) {
            if (!Array.isArray(items) || items.length === 0) return "—";
            return (
                items
                    .map((i) => i.orgBoundaryName || "")
                    .filter(Boolean)
                    .join(", ") || "—"
            );
        },
        async handleAutoMatch() {
            const targetConfigIdxs = this.factorMappingUnmappedItems.map((i) => i.configIdx).filter(Boolean);
            if (targetConfigIdxs.length === 0) return;
            this.autoMatchLoading = true;
            try {
                const res = await carbonApi.activityConfig.autoMatchFactors({
                    orgIdx: this.form.orgIdx,
                    targetConfigIdxs,
                    apply: true,
                });
                unwrapResponse(res);
                this.$bvToast.toast("자동 매칭이 적용되었습니다.", {
                    title: "완료",
                    variant: "success",
                    autoHideDelay: 3000,
                });
                // pre-check 재조회
                const preRes = await carbonApi.workflow.getPreCheck({
                    orgIdx: this.form.orgIdx,
                    fromMonth: this.form.fromMonth,
                    toMonth: this.form.toMonth,
                });
                this.preCheckData = unwrapResponse(preRes);
            } catch (e) {
                const msg = extractErrorMessage(e, "자동 매칭에 실패했습니다.");
                this.$bvToast.toast(msg, {
                    title: "오류",
                    variant: "danger",
                    autoHideDelay: 4000,
                });
            } finally {
                this.autoMatchLoading = false;
            }
        },
        handleCreateWorkflow() {
            this.emitSave();
        },
        emitSave() {
            const payload = {
                orgIdx: this.form.orgIdx,
                workflowName: this.form.workflowName.trim(),
                fromMonth: this.form.fromMonth.trim(),
                toMonth: this.form.toMonth.trim(),
            };
            if (this.form.remarks && this.form.remarks.trim()) {
                payload.remarks = this.form.remarks.trim();
            }
            if (!this.pathwayFieldDisabled) {
                if (this.form.pathwayIdx != null) {
                    payload.pathwayIdx = Number(this.form.pathwayIdx);
                } else if (this.isEditMode) {
                    payload.pathwayIdx = null;
                }
            }
            this.$emit("save", payload);
        },
        handleSave() {
            if (!this.validateStep1()) return;
            this.emitSave();
        },
    },
};
</script>

<style scoped>
/* === 1단계: 폼 2열 레이아웃, 요소 간격 증가, 인풋 그레이 배경 === */
.step1-form .form-group {
    margin-bottom: 1.5rem;
}

.step1-form-label {
    font-size: 12px;
    font-weight: 600;
    color: #0f172a;
}

.pathway-form-group .pathway-hint {
    font-size: 11px;
    margin-top: 0.35rem;
}
.step1-form .form-group:last-child {
    margin-bottom: 0;
    margin-top: 1rem;
}

.step1-form .row + .row {
    margin-top: 1rem;
}

/* 인풋 요소 그레이 배경 */
.step1-form .form-control,
.step1-form input.form-control,
.step1-form select.form-control,
.step1-form textarea.form-control {
    background-color: #f1f5f9;
    border-color: #e2e8f0;
}

.step1-form .form-control:focus,
.step1-form input.form-control:focus,
.step1-form select.form-control:focus,
.step1-form textarea.form-control:focus {
    background-color: #fff;
    border-color: #3b82f6;
}

/* DatePicker 내부 인풋 */
.step1-form ::v-deep .mx-input {
    background-color: #f1f5f9 !important;
    border-color: #e2e8f0;
}

.step1-form ::v-deep .mx-input:focus {
    background-color: #fff !important;
    border-color: #3b82f6;
}

.text-danger {
    color: #dc2626;
}

/* === 2단계: 준비 상태 점검 === */
.pre-check-step {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.pre-check-desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

/* 컨텍스트 헤더 - 다크 바 */
.pre-check-context {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border-radius: 10px;
}

.pre-check-context-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.pre-check-context-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(16, 185, 129, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #34d399;
    font-size: 14px;
    flex-shrink: 0;
}

.pre-check-context-label {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    letter-spacing: 0.03em;
    margin-bottom: 3px;
}

.pre-check-context-value {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
}

.context-org-name {
    font-weight: 700;
    color: #ffffff;
}

.context-separator {
    color: #64748b;
    font-weight: 400;
}

.context-period {
    font-weight: 500;
    color: #cbd5e1;
}

/* 로딩 상태 */
.pre-check-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 52px 24px;
}

.loading-dots {
    display: flex;
    gap: 8px;
}

.loading-dots span {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #3b82f6;
    animation: dotBounce 1.2s infinite ease-in-out;
}

.loading-dots span:nth-child(1) {
    animation-delay: 0s;
}
.loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
}
.loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes dotBounce {
    0%,
    80%,
    100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.loading-text {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
}

/* 에러 상태 */
.pre-check-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 28px 24px;
    background: linear-gradient(135deg, #fef2f2 0%, #ffffff 60%);
    border-radius: 16px;
    box-shadow: 0 2px 16px rgba(220, 38, 38, 0.1);
    color: #dc2626;
    font-size: 14px;
    font-weight: 500;
}

.error-icon-wrap {
    font-size: 18px;
    flex-shrink: 0;
}

/* 심플 카드 3열 그리드 */
.simple-cards-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

/* 점검 카드 공통 */
.pre-check-card {
    background: #ffffff;
    border: none;
    border-radius: 10px;
    padding: 14px 14px 12px;
    transition:
        box-shadow 0.2s,
        transform 0.2s;
    box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.07),
        0 1px 2px rgba(0, 0, 0, 0.04);
}

.pre-check-card:hover {
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.1),
        0 1px 4px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
}

/* factorMapping 자동 매칭 버튼 */
.card-auto-match {
    margin-top: 10px;
}

.card-auto-match-btn {
    font-size: 11px;
    font-weight: 700;
    border-radius: 8px;
    padding: 4px 12px;
}

/* 상태 뱃지 - pill 형태 */
.card-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 20px;
    margin-bottom: 10px;
}

.card-status-badge i {
    font-size: 13px;
}

.badge-pass {
    background: #d1fae5;
    color: #059669;
}

.badge-warn {
    background: #fef3c7;
    color: #d97706;
}

.badge-fail {
    background: #fee2e2;
    color: #dc2626;
}

.card-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 5px;
}

.card-message {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    color: #64748b;
}

.card-message.msg-pass {
    color: #64748b;
}
.card-message.msg-warn {
    color: #78350f;
}
.card-message.msg-fail {
    color: #991b1b;
}

.card-meta-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    color: #0d9488;
    padding: 0;
    margin-top: 8px;
}

.card-meta-pill i {
    font-size: 10px;
    opacity: 0.8;
}

.card-detail-text {
    font-size: 11px;
    color: #64748b;
    margin-top: 6px;
    line-height: 1.5;
}

/* === DatePicker: width만 지정 === */
::v-deep .mx-datepicker {
    width: 100%;
}

/* === Modal body: 1단계는 좌우 여백 축소로 공간 효율 확보 === */

.step1-form .row {
    margin-left: -8px;
    margin-right: -8px;
}

.step1-form .row > [class*="col-"] {
    padding-left: 8px;
    padding-right: 8px;
}
</style>
